import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { FieldError } from '@/components/FormField';
import { ApiError } from '@/features/auth/api';
import { formatAddressLines } from '@/features/customers/format';
import { useCustomer, type Customer } from '@/features/customers/api';
import { PartyDetails, type PartyView } from '@/features/invoices/PartyDetails';
import { BuyerPicker, type BuyerRef } from './BuyerPicker';
import { fieldErrorKey } from './fieldErrors';

/**
 * The composer's buyer section, styled to match the KSeF invoice-detail party card. The picked
 * customer is previewed as the same label/value list the read-only invoice view uses (via the shared
 * {@link PartyDetails}), with a "Change" picker and an "Edit" action in the header. The full customer
 * is fetched by id so the preview survives a reload (the draft only stores the id + a label).
 */
export function BuyerCard({
  buyer,
  onSelect,
  disabled,
  invalid,
}: {
  buyer: BuyerRef | null;
  onSelect: (buyer: BuyerRef | null) => void;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  const query = useCustomer(buyer?.id ?? null);
  const customer = query.data ?? null;

  // If the linked customer was deleted out from under the draft (its fetch 404s), it no longer
  // exists — drop the buyer so the composer shows the "choose a buyer" state and autosave persists
  // customerId=null, instead of freezing on the removed buyer's stale denormalized label until a
  // hard reload. Only a definitive 404 clears it; a transient/network error keeps the current buyer.
  // Skipped while disabled (a conflicted / read-only draft mustn't be mutated). A per-id guard fires
  // this at most once per buyer so StrictMode's double-invoke can't double-toast.
  const missing =
    buyer != null &&
    !disabled &&
    query.isError &&
    query.error instanceof ApiError &&
    query.error.status === 404;
  const clearedIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (missing && buyer != null && clearedIdRef.current !== buyer.id) {
      clearedIdRef.current = buyer.id;
      onSelect(null);
      toast.error(t('invoices.compose.buyer.removed'));
    }
  }, [missing, buyer, onSelect, t]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>{t('invoices.compose.sections.buyer')}</CardTitle>
        {buyer ? (
          <CardAction>
            <BuyerPicker
              value={buyer}
              onSelect={onSelect}
              disabled={disabled}
              align="end"
              contentClassName="w-72"
              trigger={
                <Button type="button" variant="outline" disabled={disabled}>
                  {t('invoices.compose.buyer.change')}
                </Button>
              }
            />
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent>
        {!buyer ? (
          <div className="space-y-2">
            <BuyerPicker value={null} onSelect={onSelect} disabled={disabled} invalid={invalid} />
            {invalid ? (
              <FieldError id="buyer-error" message={t(fieldErrorKey('NO_BUYER'))} />
            ) : null}
          </div>
        ) : query.isPending ? (
          <div className="space-y-2 py-1">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-1/3" />
          </div>
        ) : (
          <PartyDetails party={toPartyView(customer, buyer)} />
        )}
      </CardContent>
    </Card>
  );
}

/** Full preview when the customer loaded; otherwise just the denormalized label the draft carries. */
function toPartyView(customer: Customer | null, buyer: BuyerRef): PartyView {
  if (!customer) return { name: buyer.label };
  return {
    name: customer.name,
    taxId: customer.nip,
    addressLines: formatAddressLines(customer.address),
    countryCode: customer.address.countryCode,
    email: customer.email,
    phone: customer.phone,
  };
}
